import React from "react";
import { Link } from "react-router-dom";
import { PageHeader, SectionLabel } from "../components/Section";
import {
  services,
  paintServices,
  specialtyServices,
  addOnServices,
  PRICING_DISCLAIMER,
} from "../data/catalog";

const PriceRow = ({ label, value, testId }) => (
  <div
    data-testid={testId}
    className="flex items-baseline justify-between py-5 border-b border-gray-900 gap-4"
  >
    <span className="text-sm font-light text-gray-300 max-w-[75%]">{label}</span>
    <span className="text-base sm:text-lg font-display font-extralight text-white whitespace-nowrap">
      {value}
    </span>
  </div>
);

const PricingBlock = ({ s, kicker, index }) => (
  <section
    data-testid={`pricing-section-${s.slug}`}
    className="px-5 sm:px-8 md:px-12 lg:px-20 py-16 md:py-20 border-b border-gray-900"
  >
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
      <div className="lg:col-span-5">
        <span className="text-[10px] font-display font-light tracking-[0.4em] text-gray-600">
          {String(index).padStart(2, "0")} / {kicker}
        </span>
        <h2 className="mt-5 font-display font-extralight uppercase tracking-[0.18em] text-white text-2xl md:text-3xl lg:text-4xl">
          {s.name}
        </h2>
        {s.badge && (
          <span className="mt-4 inline-block px-3 py-1 border border-gray-400 text-[10px] font-display font-light uppercase tracking-[0.3em] text-gray-200">
            {s.badge}
          </span>
        )}
        <p className="mt-6 text-sm font-light leading-relaxed text-gray-400 max-w-md">
          {s.summary}
        </p>
        {s.duration && (
          <p className="mt-4 text-[10px] font-display font-light uppercase tracking-[0.4em] text-gray-600">
            {s.duration}
          </p>
        )}
      </div>
      <div className="lg:col-span-7">
        <p className="text-[10px] font-display font-light uppercase tracking-[0.4em] text-gray-500 mb-4">
          Starting At
        </p>
        {s.pricing && s.pricing.length > 0 ? (
          <div className="border-t border-gray-900">
            {s.pricing.map((p, idx) => (
              <PriceRow
                key={idx}
                testId={`price-row-${s.slug}-${idx}`}
                label={p.vehicle}
                value={`Starting at $${p.price}`}
              />
            ))}
          </div>
        ) : (
          <p className="text-lg font-display font-extralight text-white border-t border-gray-900 pt-4">
            {s.priceLabel || "Custom Quote"}
          </p>
        )}
        {s.footnote && (
          <p className="mt-5 text-xs font-light text-gray-500 leading-relaxed">{s.footnote}</p>
        )}
      </div>
    </div>
  </section>
);

const Pricing = () => {
  return (
    <div data-testid="pricing-page">
      <PageHeader
        testId="pricing-header"
        eyebrow="Pricing / Transparent"
        title={<>Clear pricing.<br />No hidden fees.</>}
        description="Every vehicle is different. The prices below are my starting points by package and vehicle size. Final pricing is confirmed before the appointment."
      />

      {services.map((s, i) => (
        <PricingBlock key={s.slug} s={s} kicker="Package" index={i + 1} />
      ))}

      <section
        data-testid="pricing-paint-header"
        className="px-5 sm:px-8 md:px-12 lg:px-20 py-14 border-b border-gray-900 bg-black"
      >
        <SectionLabel testId="pricing-paint-label">Paint Correction & Coatings</SectionLabel>
      </section>
      {paintServices.map((s, i) => (
        <PricingBlock key={s.slug} s={s} kicker="Paint" index={services.length + 1 + i} />
      ))}

      {/* Specialty */}
      <section className="px-5 sm:px-8 md:px-12 lg:px-20 py-16 md:py-20 border-b border-gray-900">
        <SectionLabel testId="pricing-specialty-label">Specialty Services</SectionLabel>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-10">
          {specialtyServices.map((sp) => (
            <div key={sp.slug} data-testid={`pricing-specialty-${sp.slug}`}>
              <h3 className="text-base font-display font-extralight uppercase tracking-[0.2em] text-white">
                {sp.name}
              </h3>
              <p className="mt-3 text-xs font-light text-gray-500 leading-relaxed">{sp.summary}</p>
              <div className="mt-5 border-t border-gray-900">
                {sp.pricing && sp.pricing.length > 0 ? (
                  sp.pricing.map((p, idx) => (
                    <PriceRow
                      key={idx}
                      testId={`pricing-specialty-row-${sp.slug}-${idx}`}
                      label={p.vehicle}
                      value={`Starting at $${p.price}`}
                    />
                  ))
                ) : (
                  <p className="text-sm font-display font-extralight text-white pt-4">
                    {sp.priceLabel}
                  </p>
                )}
              </div>
              {sp.addOnNote && (
                <p className="mt-4 text-[10px] font-display font-light uppercase tracking-[0.3em] text-gray-500">
                  {sp.addOnNote}
                </p>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Add-ons */}
      <section className="px-5 sm:px-8 md:px-12 lg:px-20 py-14 border-b border-gray-900">
        <SectionLabel testId="pricing-addon-label">Add-On Services</SectionLabel>
        <p className="mt-4 text-xs font-light text-gray-500 leading-relaxed max-w-2xl">
          Booked alongside another qualifying appointment.
        </p>
        <div className="mt-8 border-t border-gray-900">
          {addOnServices.map((a, i) => (
            <PriceRow
              key={i}
              testId={`pricing-addon-row-${i}`}
              label={a.name}
              value={a.priceLabel}
            />
          ))}
        </div>
      </section>

      {/* Disclaimer */}
      <section className="px-5 sm:px-8 md:px-12 lg:px-20 py-12 border-b border-gray-900">
        <p
          data-testid="pricing-disclaimer"
          className="text-xs font-light text-gray-500 leading-relaxed max-w-3xl"
        >
          {PRICING_DISCLAIMER}
        </p>
      </section>

      <section
        data-testid="pricing-cta-section"
        className="px-5 sm:px-8 md:px-12 lg:px-20 py-20 md:py-24 text-center"
      >
        <p className="text-[10px] font-display font-light uppercase tracking-[0.4em] text-gray-500 mb-6">
          Final price confirmed before the appointment
        </p>
        <h2 className="font-display font-extralight uppercase tracking-[0.18em] text-white text-2xl sm:text-4xl lg:text-5xl leading-[1.15] max-w-3xl mx-auto">
          Tell me about your vehicle.<br />I&apos;ll send you a clear, honest quote.
        </h2>
        <Link
          to="/contact"
          data-testid="pricing-cta-btn"
          className="mt-10 inline-flex px-12 py-5 border border-gray-200 text-[11px] font-display font-light uppercase tracking-[0.3em] text-white hover:bg-white hover:text-black transition-all"
        >
          Request a Quote
        </Link>
      </section>
    </div>
  );
};

export default Pricing;
